import { computed, ref } from '@vue/composition-api';
import { defineStore } from 'pinia';
import { capitalize } from 'lodash';
import { LOCALIZATION_STORE_ID } from '@/constants/storeIds';
import { fetchTranslationsService, updateTranslationService, synchronizeTranslationService, createTranslationService } from '@/services/application-service/localizationRequests';
import { useModuleStore } from '@/modules/builder/store/moduleStore';
import { updateModuleService } from '@/services/application-service/moduleRequests';

export const useLocalizationStore = defineStore(LOCALIZATION_STORE_ID, () => {
    const moduleStore = useModuleStore();
    
    /** @type {import('./types/localizationStore').Translations} */
    const translations = ref([]);

    const localizationSettings = computed(() => moduleStore.moduleDetails?.settings?.localization || {});

    const supportedLanguages = computed(() => localizationSettings.value?.supportedLanguages || []);

    const moduleTranslations = computed(() => {
        /** @type {import('./types/localizationStore').ITranslation[]} */
        let keys = [];
        if (moduleStore.screenFieldsMeta?.length && translations.value?.length) {
            keys = translations.value.map(translation => {
                let translationName = '';
                const matchedField = moduleStore.screenFieldsMeta.find(field => field.id === translation.field_id);
                let matchedSubField = null;
                if (matchedField?.subFields?.length) {
                    matchedSubField = matchedField.subFields.find(subField => translation.reference.startsWith(subField.id));
                }
                const fieldPart = translation.reference.split('_').at(-1);
                let fieldType = 'N/A';
                if (matchedField) {
                    translationName = `${matchedField.nodeName} > ${matchedField.name}`;
                    if (matchedSubField) {
                        translationName += ` > ${matchedSubField.name}`;
                    }
                    translationName += ` > ${capitalize(fieldPart)}`;
                    fieldType = matchedSubField ? matchedSubField.type : matchedField.type;
                }
                return {
                    ...translation,
                    fieldType,
                    name: translation.name || translationName
                };
            });
            keys = keys.filter(key => key.name);
        }
        return keys;
    }); 

    const fetchTranslations = async (appId, moduleId) => {
        try {
            let { data: { data } } = await fetchTranslationsService(appId, moduleId);
            translations.value = data;
        } catch (err) {
            console.error(err);
        }
    };

    const updateTranslation = async (appId, moduleId, translationId, payload, params) => {
        try {
            await updateTranslationService(appId, moduleId, translationId, payload, params);
            const updatedTranslation = translations.value.find(translation => translation.id === translationId);
            if (updatedTranslation) {
                updatedTranslation.translations[params.lang_id] = payload.translation;
            }
        } catch (err) {
            console.error(err);
        }
    };

    const createTranslation = async (appId, moduleId, payload) => {
        await createTranslationService(appId, moduleId, payload);
        await fetchTranslations(appId, moduleId);
    };

    const synchronizeTranslation = async (appId, moduleId) => {
        try {
            await synchronizeTranslationService(appId, moduleId);
            await Promise.all([
                moduleStore.fetchModule(appId, moduleId),
                fetchTranslations(appId, moduleId)
            ]);
        } catch (err) {
            console.error(err);
            throw new Error('Translations synchronization failed');
        }
    };

    const updateLocalizationSettings = async (appId, moduleId, updatedLocalizationSettings) => {
        await updateModuleService(appId, moduleId, {
            settings: {
                ...moduleStore.moduleDetails.settings,
                localization: updatedLocalizationSettings
            }
        });
    };

    return {
        translations,
        moduleTranslations,
        localizationSettings,
        supportedLanguages,
        fetchTranslations,
        updateTranslation,
        synchronizeTranslation,
        createTranslation,
        updateLocalizationSettings
    };
});
